import { useEffect, useRef } from "react";

// Full-screen photo viewer for a single row of photos.
// Arrows / swipe step through the row; Esc, the close button or a backdrop click closes it.
function Lightbox({ photos, index, onClose, onNavigate }) {
    const dialogRef = useRef(null);
    const closeRef = useRef(null);
    const touchX = useRef(null);

    const photo = photos[index];
    const hasMany = photos.length > 1;
    const step = (dir) => onNavigate((index + dir + photos.length) % photos.length);

    // Lock page scroll while open, and hand focus back to the photo on close
    useEffect(() => {
        const previousFocus = document.activeElement;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        closeRef.current?.focus();
        return () => {
            document.body.style.overflow = previousOverflow;
            previousFocus?.focus?.();
        };
    }, []);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            else if (e.key === "ArrowRight" && hasMany) step(1);
            else if (e.key === "ArrowLeft" && hasMany) step(-1);
            else if (e.key === "Tab") {
                // Keep keyboard focus inside the dialog
                const focusables = dialogRef.current.querySelectorAll("button");
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    });

    const onTouchStart = (e) => {
        touchX.current = e.touches[0].clientX;
    };

    const onTouchEnd = (e) => {
        if (touchX.current === null || !hasMany) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 50) step(dx < 0 ? 1 : -1);
        touchX.current = null;
    };

    return (
        <div
            ref={dialogRef}
            className="lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={photo.alt}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
        >
            <button ref={closeRef} type="button" className="lightbox-btn lightbox-close" onClick={onClose} aria-label="Close">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="20" height="20">
                    <line x1="5" y1="5" x2="19" y2="19" />
                    <line x1="19" y1="5" x2="5" y2="19" />
                </svg>
            </button>

            {hasMany && (
                <button type="button" className="lightbox-btn lightbox-prev" onClick={() => step(-1)} aria-label="Previous photo">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="22" height="22">
                        <polyline points="15 18 9 12 15 6" />
                    </svg>
                </button>
            )}

            <figure className="lightbox-figure">
                <img key={photo.src} src={photo.src} alt={photo.alt} className="lightbox-img" />
                <figcaption className="lightbox-caption">
                    {photo.alt}
                    {hasMany && <span className="lightbox-count">{index + 1} / {photos.length}</span>}
                </figcaption>
            </figure>

            {hasMany && (
                <button type="button" className="lightbox-btn lightbox-next" onClick={() => step(1)} aria-label="Next photo">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="22" height="22">
                        <polyline points="9 18 15 12 9 6" />
                    </svg>
                </button>
            )}
        </div>
    );
}

export default Lightbox;
